import React, { useState, useRef, startTransition } from 'react';
import Lottie from 'lottie-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOidc } from '@axa-fr/react-oidc';
import HomeLayout from '../../Layout/HomeLayout';
import HomeHeader from '../../CustomComponents/HomeHeader';
import DynamicCard from '../../CustomComponents/DynamicCard';
import CreditLimitAnim from '../../../assets/animations/credit_limit.json'
import CreditScoreAnim from '../../../assets/animations/credit_score.json'
import PointsAnim from '../../../assets/animations/points.json'
import VectorBackground from '../../../assets/images/ftu_leaf.png';
import { width, height } from 'components/CustomComponents/Card/CardStyles';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';

const SlideLayout = ({ slide, slideIndex }) => {
  const { t } = useTranslation();
  const modalRef = useRef(null);
  const mediaBottomMarginClass = slideIndex === 0
    ? 'mb-[80px]'
    : slideIndex === 1
      ? 'mb-[96px]'
      : 'mb-[80px]';
  return (
    <div className="flex md:flex flex-col justify-start items-center h-full md:px-[2vw] px-4 pb-[2vh]">
      <div className="text-left md:max-w-[40vw] max-w-full md:min-w-[400px] min-w-0 mt-[24px] md:ml-[40px] ml-4 min-h-[120px] md:min-h-[160px] flex-shrink-0 mb-[20px]">
        <h1 className="font-serif-pro font-normal text-[clamp(16px,4.5vw,26px)] leading-[clamp(22px,5.5vw,32px)] tracking-normal mb-2 whitespace-pre-line break-words">
          <span className="text-[#FFFFFF]">{slide.content.title.first}</span>
          {slide.content.title.second && (
            <>
              {slide.content.title.first && slide.content.title.first.trim().length > 0 &&
                !slide.content.title.first.includes('\n') &&
                !slide.content.title.second.includes('\n') && <br />}
              <span className="text-[#C1ECD9]">
                {slide.content.title.first && (slide.content.title.first.includes('\n') || slide.content.title.second.includes('\n'))
                  ? ` ${slide.content.title.second}`
                  : slide.content.title.second}
              </span>

            </>
          )}
          <span className="text-[#FFFFFF]">
            {slide.content.title.third}
          </span>
        </h1>
        <div className="text-[#FFFFFF] font-serif-pro font-normal text-[clamp(12px,1.2vw,16px)] leading-[clamp(18px,1.6vw,22px)] word-break-normal mb-[20px]">
          <p>
            {slide.content.description.split('\n').map((line, index) => (
              <span key={index}>
                {line.trim()}
                {index < slide.content.description.split('\n').length - 1 && <br />}
              </span>
            ))}
          </p>
        </div>
      </div>

      {/* Media (Lottie preferred, fallback to Image) */}
      <div className={`${mediaBottomMarginClass} w-full flex justify-center`}>
        <div className="w-[clamp(180px,40vw,270px)] max-w-[clamp(180px,40vw,270px)] h-auto flex-shrink-0">
          {slide.animationData ? (
            <Lottie
              animationData={slide.animationData}
              loop
              autoplay
              style={{ width: '100%', height: '100%' }}
            />
          ) : (
            <img
              src={slide.image}
              alt={slide.content.title.second || "Secured credit card feature"}
              className="w-full h-full object-contain"
            />
          )}
        </div>
      </div>
    </div>


  );
};

const FTUCarousel = ({ onSkipClick, onKudzuPlusClick }) => {
    const { t } = useTranslation();
    useDocumentTitle(t('page_title_secured_credit_welcome'));

  const navigate = useNavigate();
 
  const { login } = useOidc();
  const modalRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Structured slide data with all content
  const landingSlidesData = [
    {
      animationData: CreditScoreAnim,
      content: {
        title: {
          first: t('SecuredCard_great_tool'),
          second: t('grow_your_credit_score'),
          third: t('financial_behavior')
        },
        description: t('')
      }
    },
    {
      animationData: CreditLimitAnim,
      content: {
        title: {
          first: t('your_initial_security_deposite'),
          second: t('least_200'),
          third: t('also_acts_as_your_cards_credit_limit')
        },
        description: t('the_more_you_deposite_the_higher')
      }
    },
    {
      animationData: PointsAnim,
      content: {
        title: {
          first: t(''),
          second: t('earn_points'),
          third: t('with_your_secured_credit_card')
        },
        description: t('money_you_spend_on_time_payments')
      }
    }
  ];


  const goToPrevSlide = () => {
    const newSlide = currentSlide === 0 ? landingSlidesData.length - 1 : currentSlide - 1;
    setCurrentSlide(newSlide);
  };

  const goToNextSlide = () => {
    const newSlide = currentSlide === landingSlidesData.length - 1 ? 0 : currentSlide + 1;
    setCurrentSlide(newSlide);
  };

  // Touch handlers for swipe functionality
  const handleTouchStart = (e) => {
    e.preventDefault();
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();

    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      goToNextSlide();
    }
    if (isRightSwipe) {
      goToPrevSlide();
    }

    // Reset values
    setTouchStart(null);
    setTouchEnd(null);
  };

  return (
 <HomeLayout>

        <div className="flex flex-col items-center justify-center lg:w-[550px] md:w-[550px] sm:w-[464px] w-full max-w-full px-4 sm:px-0 mx-auto animate__animated animate__fadeInRight">
        <DynamicCard
          header={false}
          footer={false}
          CardRadius={true}
          cardWidth={width.homeCard}
          cardHeight={height.default_card}
          HeaderContent={false}
          isDark={true}
        >
   
        <div
          className="relative px-4 sm:px-6 overflow-hidden"
          style={{
            background: '#324261',
            paddingTop: '40px',
            paddingBottom: '80px',
            borderTopRightRadius: '36px',
            borderBottomLeftRadius: '36px',
          }}
        
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `url(${VectorBackground})`,
              backgroundSize: '500px 380px',
              backgroundPosition: 'right 60px top 40px',
              backgroundRepeat: 'no-repeat',
              borderTopRightRadius: '36px',
              borderBottomLeftRadius: '36px',
              pointerEvents: 'none'
            }}
          />


          <div className="flex items-start justify-center overflow-hidden" style={{ zIndex: 100 }}>

            <div
              className="flex h-full transition-transform duration-300 ease-out will-change-transform"
              style={{
                transform: `translateX(-${currentSlide * 100}%)`,
                touchAction: 'pan-y'
              }}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {landingSlidesData.map((slide, index) => (
                <div key={index} className="w-full md:w-full flex-shrink-0 min-w-full">
                  <SlideLayout slide={slide} slideIndex={index} />
                </div>
              ))}

            </div>



            {/* Pagination Dots and Actions (fixed at dialog bottom on desktop) */}
            <div className="hidden md:flex w-full justify-center absolute bottom-0 left-0 z-30 pb-8">
              <div className="flex flex-col items-center" style={{ width: '500px', maxWidth: '85vw' }}>
                <div className="flex gap-2">
                  {landingSlidesData.map((_, index) => (
                    <button
                      key={index}
                      className={`rounded-full border-none cursor-pointer transition-all duration-300 w-[6px] h-[6px] ${index === currentSlide
                          ? 'bg-[#30BC89]'
                          : 'bg-[rgba(38,43,58,0.3)] hover:bg-[rgba(38,43,58,0.5)]'
                        }`}
                      onClick={() => setCurrentSlide(index)}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
                <div className="flex flex-col gap-3 mt-3 items-center">
                  <button
                    onClick={goToNextSlide}
                    className="w-[350px] h-11 mx-[20px] rounded-none font-hanken-grotesk font-semibold text-sm transition-all duration-300 hover:opacity-90 active:scale-95 bg-[#2965FB] text-white border-0 shadow-sm hover:shadow-md mt-[16px]"
                    type="button"
                  >
                    Next
                  </button>
                  <button
                    onClick={goToPrevSlide}
                    className="w-[200px] h-11 rounded-none font-hanken-grotesk font-semibold text-sm transition-all duration-300 hover:opacity-90 active:scale-95 bg-transparent text-[#94C3E8] shadow-sm hover:shadow-md mb-2"
                    type="button"
                  >
                    Skip
                  </button>
                </div>
              </div>
            </div>

            {/* Mobile controls: fixed at dialog bottom on small screens */}
            <div className="md:hidden w-full flex flex-col items-center gap-3 absolute bottom-0 left-0 px-4 z-30 pb-8">
              <div className="flex gap-2">
                {landingSlidesData.map((_, index) => (
                  <button
                    key={index}
                    className={`rounded-full border-none cursor-pointer transition-all duration-300 w-[6px] h-[6px] ${index === currentSlide
                        ? 'bg-[#30BC89]'
                        : 'bg-[rgba(38,43,58,0.3)] hover:bg-[rgba(38,43,58,0.5)]'
                      }`}
                    onClick={() => setCurrentSlide(index)}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
              <button
                onClick={goToNextSlide}
                className="w-full max-w-[360px] h-11 rounded-none font-hanken-grotesk font-semibold text-sm transition-all duration-300 hover:opacity-90 active:scale-95 bg-[#2965FB] text-white border-0 shadow-sm hover:shadow-md"
                type="button"
              >
                Next
              </button>
              <button
                onClick={goToPrevSlide}
                className="w-full max-w-[360px] h-11 rounded-none font-hanken-grotesk font-semibold text-sm transition-all duration-300 hover:opacity-90 active:scale-95 bg-transparent text-[#94C3E8] shadow-sm hover:shadow-md mb-2"
                type="button"
              >
                Skip
              </button>
            </div>


          </div>
        </div>
     
       </DynamicCard>
      </div>

</HomeLayout>
  );
};

export default FTUCarousel;
